import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { trackGA4Event, setGA4UserProperties, GA4_EVENTS } from '../../analytics/ga4';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import STATE_BOUNDS from './stateBounds';

const { LatLng, LatLngBounds } = sdkTypes;

// How many in-state trailers to preview on the renter's final onboarding step.
const RENTER_PREVIEW_LIMIT = 6;

// ================ Action types ================ //

export const SAVE_ROLE_REQUEST = 'app/OnboardingPage/SAVE_ROLE_REQUEST';
export const SAVE_ROLE_SUCCESS = 'app/OnboardingPage/SAVE_ROLE_SUCCESS';
export const SAVE_ROLE_ERROR = 'app/OnboardingPage/SAVE_ROLE_ERROR';

export const SAVE_SMS_REQUEST = 'app/OnboardingPage/SAVE_SMS_REQUEST';
export const SAVE_SMS_SUCCESS = 'app/OnboardingPage/SAVE_SMS_SUCCESS';
export const SAVE_SMS_ERROR = 'app/OnboardingPage/SAVE_SMS_ERROR';

export const FETCH_PREVIEW_REQUEST = 'app/OnboardingPage/FETCH_PREVIEW_REQUEST';
export const FETCH_PREVIEW_SUCCESS = 'app/OnboardingPage/FETCH_PREVIEW_SUCCESS';
export const FETCH_PREVIEW_ERROR = 'app/OnboardingPage/FETCH_PREVIEW_ERROR';

// ================ Reducer ================ //

const initialState = {
  saveRoleInProgress: false,
  saveRoleError: null,
  saveSmsInProgress: false,
  saveSmsError: null,
  previewInProgress: false,
  previewError: null,
  previewResultIds: [],
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_ROLE_REQUEST:
      return { ...state, saveRoleInProgress: true, saveRoleError: null };
    case SAVE_ROLE_SUCCESS:
      return { ...state, saveRoleInProgress: false };
    case SAVE_ROLE_ERROR:
      return { ...state, saveRoleInProgress: false, saveRoleError: payload };

    case SAVE_SMS_REQUEST:
      return { ...state, saveSmsInProgress: true, saveSmsError: null };
    case SAVE_SMS_SUCCESS:
      return { ...state, saveSmsInProgress: false };
    case SAVE_SMS_ERROR:
      return { ...state, saveSmsInProgress: false, saveSmsError: payload };

    case FETCH_PREVIEW_REQUEST:
      return { ...state, previewInProgress: true, previewError: null };
    case FETCH_PREVIEW_SUCCESS:
      return {
        ...state,
        previewInProgress: false,
        previewResultIds: (payload?.data?.data || []).map(l => l?.id) || [],
      };
    case FETCH_PREVIEW_ERROR:
      // Never blank the renter page on a listings failure; keep prior ids.
      return { ...state, previewInProgress: false, previewError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const saveRoleInProgressSelector = state => state.OnboardingPage.saveRoleInProgress;
export const saveSmsInProgressSelector = state => state.OnboardingPage.saveSmsInProgress;
export const previewInProgressSelector = state => state.OnboardingPage.previewInProgress;
export const getPreviewResultIds = state => state.OnboardingPage?.previewResultIds || [];
export const getPreviewListings = state => getListingsById(state, getPreviewResultIds(state));

// ================ Action creators ================ //

export const saveRoleRequest = () => ({ type: SAVE_ROLE_REQUEST });
export const saveRoleSuccess = () => ({ type: SAVE_ROLE_SUCCESS });
export const saveRoleError = error => ({ type: SAVE_ROLE_ERROR, payload: error, error: true });

export const saveSmsRequest = () => ({ type: SAVE_SMS_REQUEST });
export const saveSmsSuccess = () => ({ type: SAVE_SMS_SUCCESS });
export const saveSmsError = error => ({ type: SAVE_SMS_ERROR, payload: error, error: true });

export const fetchPreviewRequest = () => ({ type: FETCH_PREVIEW_REQUEST });
export const fetchPreviewSuccess = response => ({ type: FETCH_PREVIEW_SUCCESS, payload: response });
export const fetchPreviewError = e => ({ type: FETCH_PREVIEW_ERROR, payload: e, error: true });

// ================ Thunks ================ //

const queryParams = {
  expand: true,
  include: ['profileImage'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

// Persist the onboarding role choice + state to publicData.
// These exact keys/values are consumed by the Zapier -> Mailchimp automation:
//   publicData.userType     = 'trailer-renter' | 'trailer-owner'
//   publicData.userLocation = full US state name (e.g. 'Massachusetts')
export const saveRoleAndState = ({ userType, userLocation }) => (dispatch, getState, sdk) => {
  dispatch(saveRoleRequest());

  const publicData = { userType };
  if (userLocation) {
    publicData.userLocation = userLocation;
  }

  return sdk.currentUser
    .updateProfile({ publicData }, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveRoleSuccess());

      // ---- ANALYTICS: role selection (renter vs owner) ----
      // Set user_type as a GA4 user property so the WHOLE funnel can be
      // segmented renter vs owner, plus a discrete select_role event.
      setGA4UserProperties({ user_type: userType });
      trackGA4Event(GA4_EVENTS.SELECT_ROLE, {
        user_type: userType,
        user_location: userLocation,
      });

      return currentUser;
    })
    .catch(e => {
      dispatch(saveRoleError(storableError(e)));
      throw e;
    });
};

// Query up to RENTER_PREVIEW_LIMIT bookable trailers scoped to the renter's
// state (via STATE_BOUNDS), so the final renter onboarding step shows real
// in-state inventory instead of dead space. Best-effort: a failure or an empty
// state never blanks the page (the hero + Haul Star decoder still render, and
// the "Browse all" CTA is always present). `pub_isInfoOnly: false` mirrors the
// SEO hubs (only real bookable listings).
export const fetchStatePreviewListings = (stateName, config) => (dispatch, getState, sdk) => {
  const bounds = stateName && STATE_BOUNDS[stateName];
  if (!bounds) {
    // No bounds for this state -> nothing to preview, but not an error.
    dispatch(fetchPreviewSuccess({ data: { data: [] } }));
    return Promise.resolve(null);
  }
  dispatch(fetchPreviewRequest());
  const [north, east, south, west] = bounds;
  const geoBounds = new LatLngBounds(new LatLng(north, east), new LatLng(south, west));
  return sdk.listings
    .query({
      bounds: geoBounds,
      pub_isInfoOnly: false,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.listing-card',
        'variants.listing-card-2x',
      ],
      ...createImageVariantConfig('listing-card', 400, 1),
      ...createImageVariantConfig('listing-card-2x', 800, 1),
      'limit.images': 1,
      perPage: RENTER_PREVIEW_LIMIT,
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      dispatch(addMarketplaceEntities(response, { listingFields }));
      dispatch(fetchPreviewSuccess(response));
      return response;
    })
    .catch(e => {
      // eslint-disable-next-line no-console
      console.log('OnboardingPage: state preview query failed (page preserved)', e && e.message);
      dispatch(fetchPreviewError(storableError(e)));
      return null;
    });
};

// Persist SMS opt-in for owners. Phone goes to protectedData.phoneNumber (same key
// the bl-sms-poller reads). TCPA proof: smsConsent boolean + smsConsentAt ISO stamp,
// preserving the original grant time on re-save.
export const saveSmsOptIn = ({ phoneNumber, smsConsent }) => (dispatch, getState, sdk) => {
  dispatch(saveSmsRequest());

  const state = getState();
  const existingPd =
    state.user?.currentUser?.attributes?.profile?.protectedData || {};

  const protectedData = { phoneNumber };
  protectedData.smsConsent = !!smsConsent;
  if (smsConsent) {
    protectedData.smsConsentAt = existingPd.smsConsentAt || new Date().toISOString();
  } else {
    protectedData.smsConsentAt = null;
    protectedData.smsConsentRevokedAt = new Date().toISOString();
  }

  return sdk.currentUser
    .updateProfile({ protectedData }, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));
      dispatch(saveSmsSuccess());

      // ---- ANALYTICS: SMS opt-in (only when consent granted) ----
      if (smsConsent) {
        trackGA4Event(GA4_EVENTS.SMS_OPT_IN, { method: 'onboarding' });
      }

      return currentUser;
    })
    .catch(e => {
      dispatch(saveSmsError(storableError(e)));
      throw e;
    });
};
